import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bold, Eye, Heading, Italic, Link as LinkIcon, List, ListOrdered, Pencil } from 'lucide-react';
import { Button } from './button';
import { Textarea } from './textarea';
import { Markdown } from './markdown';
import { cn } from '../../lib/utils';

/**
 * One toolbar button's job: what to put before and after the selection, and
 * what to leave selected when there was nothing selected to wrap.
 */
interface Action {
  key: string;
  icon: typeof Bold;
  before: string;
  after: string;
  /** Placeholder text dropped in when the button is pressed on an empty cursor. */
  placeholderKey: string;
  /** Line-level formatting is applied to the start of the line, not around it. */
  linePrefix?: boolean;
}

const ACTIONS: Action[] = [
  { key: 'bold', icon: Bold, before: '**', after: '**', placeholderKey: 'markdownEditor.boldText' },
  { key: 'italic', icon: Italic, before: '_', after: '_', placeholderKey: 'markdownEditor.italicText' },
  { key: 'heading', icon: Heading, before: '## ', after: '', placeholderKey: 'markdownEditor.headingText', linePrefix: true },
  { key: 'bulletList', icon: List, before: '- ', after: '', placeholderKey: 'markdownEditor.listItem', linePrefix: true },
  { key: 'numberedList', icon: ListOrdered, before: '1. ', after: '', placeholderKey: 'markdownEditor.listItem', linePrefix: true },
  { key: 'link', icon: LinkIcon, before: '[', after: '](https://)', placeholderKey: 'markdownEditor.linkText' },
];

interface MarkdownEditorProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
}

/**
 * A Markdown box with a formatting toolbar and a preview.
 *
 * Markdown rather than a true contenteditable WYSIWYG: what an admin writes
 * here is rendered straight onto a public page, and Markdown has no way to
 * carry a `<script>` or an `onerror=` through — the renderer refuses embedded
 * HTML (see Markdown). The toolbar is what makes it feel like a rich editor:
 * nobody has to know the syntax to put a heading in.
 */
export function MarkdownEditor({
  id,
  value,
  onChange,
  placeholder,
  rows = 10,
  className,
}: MarkdownEditorProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [previewing, setPreviewing] = useState(false);

  const apply = useCallback((action: Action) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd } = textarea;
    const selected = value.slice(selectionStart, selectionEnd);
    const placeholderText = t(action.placeholderKey);

    let start = selectionStart;
    let inserted: string;

    if (action.linePrefix) {
      // A heading or a list marks the line it is on, so the insertion point
      // moves back to where that line begins.
      start = value.lastIndexOf('\n', selectionStart - 1) + 1;
      const body = selected || placeholderText;
      inserted = action.before + value.slice(start, selectionStart) + body;
    } else {
      inserted = action.before + (selected || placeholderText) + action.after;
    }

    const next = value.slice(0, start) + inserted + value.slice(selectionEnd);
    onChange(next);

    // Leave the newly written words selected, so typing replaces the
    // placeholder and the change reads as one action rather than two.
    const bodyStart = start + action.before.length + (action.linePrefix ? selectionStart - start : 0);
    const bodyLength = (selected || placeholderText).length;
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(bodyStart, bodyStart + bodyLength);
    });
  }, [value, onChange, t]);

  return (
    <div className={cn('rounded-md border', className)}>
      <div className="flex flex-wrap items-center gap-1 border-b p-1">
        {ACTIONS.map((action) => (
          <Button
            key={action.key}
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={previewing}
            title={t(`markdownEditor.${action.key}`)}
            aria-label={t(`markdownEditor.${action.key}`)}
            onClick={() => apply(action)}
          >
            <action.icon className="h-4 w-4" />
          </Button>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="ml-auto h-8 gap-1.5"
          onClick={() => setPreviewing((current) => !current)}
        >
          {previewing ? <Pencil className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {previewing ? t('markdownEditor.write') : t('markdownEditor.preview')}
        </Button>
      </div>

      {previewing ? (
        <div className="min-h-32 p-3">
          {value.trim() ? (
            <Markdown>{value}</Markdown>
          ) : (
            <p className="text-sm text-muted-foreground">{t('markdownEditor.nothingToPreview')}</p>
          )}
        </div>
      ) : (
        <Textarea
          id={id}
          ref={textareaRef}
          value={value}
          rows={rows}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="rounded-none border-0 font-mono text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      )}
    </div>
  );
}
